import { TeamProbeUsageSchema } from '@shared/dto/teams';
import type {
  CreateTeamSecretRequest,
  TeamSecretDTO,
  TeamSecretSummaryDTO,
  TeamSecretValueDTO,
  UpdateTeamSecretRequest,
} from '@shared/dto/teamSecrets';
import type {
  CreateTeamDTO,
  TeamDTO,
  TeamInferenceProviderKey,
  TeamInferenceProvidersResponse,
  TeamProbeUsageDTO,
  TeamRedteamProviderResponse,
  TeamWithoutMembersDTO,
  UpdateTeamDTO,
  UpdateTeamInferenceProviderRequest,
  UpdateTeamRedteamProviderRequest,
} from '@shared/dto/teams';

import { callApi, callApiJson } from '../api';

export const fetchTeams = async (): Promise<TeamDTO[]> => {
  return await callApiJson('/teams');
};

export const getTeam = async (teamId: string): Promise<TeamDTO> => {
  return await callApiJson(`/teams/${teamId}`);
};

export const getTeamUsage = async (teamId: string): Promise<TeamProbeUsageDTO> => {
  const data = await callApiJson(`/teams/${teamId}/usage`);
  return TeamProbeUsageSchema.parse(data);
};

export const getTeamRedteamProvider = async (
  teamId: string,
): Promise<TeamRedteamProviderResponse> => {
  return callApiJson(`/teams/${encodeURIComponent(teamId)}/redteam-provider`);
};

export const getTeamInferenceProviders = async (
  teamId: string,
): Promise<TeamInferenceProvidersResponse> => {
  return callApiJson(`/teams/${teamId}/inference-providers`);
};

export const createTeam = async (data: CreateTeamDTO): Promise<TeamDTO> => {
  return await callApiJson('/teams', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateTeam = async (id: string, changes: UpdateTeamDTO): Promise<TeamDTO> => {
  return await callApiJson(`/teams/${id}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
};

export const updateTeamRedteamProvider = async (
  teamId: string,
  data: UpdateTeamRedteamProviderRequest,
): Promise<TeamRedteamProviderResponse> => {
  return callApiJson(`/teams/${encodeURIComponent(teamId)}/redteam-provider`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const updateTeamInferenceProvider = async (
  teamId: string,
  key: TeamInferenceProviderKey,
  data: UpdateTeamInferenceProviderRequest,
): Promise<TeamInferenceProvidersResponse> => {
  return callApiJson(`/teams/${teamId}/inference-providers/${key}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  await callApi(`/teams/${teamId}`, {
    method: 'DELETE',
  });
};

export const addTeamMember = async (
  teamId: string,
  userId: string,
  roleId?: string,
): Promise<void> => {
  await callApi(`/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, roleId }),
  });
};

export const removeTeamMember = async (teamId: string, userId: string): Promise<void> => {
  await callApi(`/teams/${teamId}/members/${userId}`, {
    method: 'DELETE',
  });
};

export const getTeamMembers = async (teamId: string): Promise<TeamDTO['members']> => {
  return await callApiJson(`/teams/${teamId}/members`);
};

export const getCurrentUserTeams = async (): Promise<TeamWithoutMembersDTO[]> => {
  return await callApiJson('/users/me/teams');
};

export const updateTeamMemberRole = async (
  teamId: string,
  userId: string,
  roleId: string,
): Promise<void> => {
  await callApi(`/teams/${teamId}/members/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ roleId }),
  });
};

export const getTeamSecrets = async (teamId: string): Promise<TeamSecretSummaryDTO[]> => {
  return callApiJson(`/teams/${encodeURIComponent(teamId)}/secrets`);
};

export const getTeamSecretValue = (teamId: string, secretId: string): Promise<TeamSecretValueDTO> =>
  callApiJson(
    `/teams/${encodeURIComponent(teamId)}/secrets/${encodeURIComponent(secretId)}/value`,
    { cache: 'no-store' },
  );

export const createTeamSecret = async (
  teamId: string,
  data: CreateTeamSecretRequest,
): Promise<TeamSecretDTO> => {
  return callApiJson(`/teams/${encodeURIComponent(teamId)}/secrets`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateTeamSecret = async (
  teamId: string,
  secretId: string,
  data: UpdateTeamSecretRequest,
): Promise<TeamSecretDTO> => {
  return callApiJson(
    `/teams/${encodeURIComponent(teamId)}/secrets/${encodeURIComponent(secretId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(data),
    },
  );
};

export const deleteTeamSecret = async (teamId: string, secretId: string): Promise<void> => {
  await callApi(`/teams/${encodeURIComponent(teamId)}/secrets/${encodeURIComponent(secretId)}`, {
    method: 'DELETE',
  });
};
